import { UseChatHelpers } from '@ai-sdk/react';
import { isNotEmpty } from 'ramda';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { Metadata, UIChatMessage } from '../Chat/chat.types';
import { SubmitArgs } from '../Chat/ChatUserMessage/ChatUserMessage.types';
import { getChatUserMessageSubmit } from '../Chat/ChatUserMessage/ChatUserMessage.utils';
import { useConfig } from '../ConfigProvider';

import { GetTranslationFn } from './ConversationQuickAction.types';
import { getTranslationText } from './ConversationQuickAction.utils';

interface UseQuickActionProps {
  sendMessage: UseChatHelpers<UIChatMessage>['sendMessage'];
}
export type UseQuickActionResult = (args: SubmitArgs, metadata?: Metadata) => Promise<void>;

export const useConversationQuickActionSubmit = ({ sendMessage }: UseQuickActionProps): UseQuickActionResult => {
  return async (args, metadata) => {
    const quickActionSubmit = getChatUserMessageSubmit(metadata?.type);
    const message = quickActionSubmit(args, metadata);
    sendMessage(message);
  };
};

export const useQuickActionTranslation = () => {
  // currently selected language
  const {
    i18n: { language },
  } = useTranslation();

  // Grabbing the customer selectable languages from config and defining first language there
  // as fallback language in case no translation is available
  // This is a safety net only because it should be enforced through UI schema validations on submission
  // Not using procureai/ui LANGUAGE_DEFAULT as this is hard mapped to `de` independent of customer configuration
  const { languages } = useConfig();
  const fallbackLanguage = isNotEmpty(languages) ? languages[0] : undefined;

  return {
    getTranslatedText: useCallback<GetTranslationFn>(
      (translations) => getTranslationText(translations, language, fallbackLanguage),
      // fallbackLanguage is static after init and should not trigger a re-render
      [language, fallbackLanguage],
    ),
  };
};
